import React, { useState, useContext } from 'react';
import getConfig from 'next/config';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Button, Message } from '@bijie/react-bnb';
import Icon from '../Icon';
import EmailVerify from '../EmailVerify';
import Timer from './Timer';
import { StoreContext } from './context';
import { Link } from '../../routes';
import { STEP_ENUM, QUESTION_STATUS_MAP } from './enum';
import { getLabel } from '../../utils/i18n';
import { fetchQuestionList, answerQuestion } from '../../actions/actionsSet/reset2fa';

import { TOAST_FADE_TIME } from '../../config/constantNg';

const {
  publicRuntimeConfig: {
    assetPrefix,
  },
} = getConfig();

const InitContainer = styled.div`
  width: 660px;
  margin: 0 auto;
  text-align: center;
  .question-img {
    width: 105px;
    height: 105px;
  }
  h1 {
    margin: 20px 0 10px 0 !important;
  }
  p {
    margin-bottom: 0;
    padding-bottom: ${({ bottom = 30 }) => `${bottom}px`};
    font-family: ${({ theme }) => theme.fontIBMRegular};
  }
  .content {
    display: flex;
    padding: 13px 20px;
    color: #DCA90A;
    background-color: rgba(220,169,10,.06);
    ul {
      margin-left: 32px;
      text-align: left;
    }
  }
  .btn-box {
    text-align: center;
    padding-top: 40px;
  }
`;
const Container = styled.div`
  position: relative;
  .serial {
    position: absolute;
    top: -20px;
    left: 0;
    color: #212833;
    font-size: 18px;
    .count {
      line-height: 43px;
      font-size: 34px;
    }
    .total {
      opacity: .3;
    }
  }
  .timer {
    margin-bottom: 30px;
    color: #F0B90B;
    text-align: right;
  }
  .question-item {
    padding: 0 160px;
    font-family: ${({ theme }) => theme.fontIBMRegular};
  }
  .question-title {
    font-size: 24px;
    line-height: 32px;
    padding-bottom: 30px;
  }
  .option-wrapper {
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
  }
  .question-option {
    display: inline-block;
    position: relative;
    width: 200px;
    padding: 30px;
    margin-top: 20px;
    text-align: center;
    font-size: 16px;
    border-radius: 3px;
    cursor: pointer;
    background-color: rgba(234, 236, 239, .3);
    &:hover {
      background-color: rgba(240, 185, 11, .2);
    }
    .icon-step1 {
      position: absolute;
      right: -14px;
      top: -14px;
      opacity: 0;
    }
    &.checked {
      background-color: rgba(240, 185, 11, .2);
      .icon-step1 {opacity: 1;}
    }
  }
  .btn-box {
    position: relative;
    text-align: right;
    padding-top: 80px;
    .error-line {
      position: absolute;
      top: 32px;
      right: 0;
      color: #F23051;
      font-family: ${({ theme }) => theme.fontIBMMedium};
    }
  }
`;
const BaseLayoutContainer = styled.div`
  display: flex;
  width: 550px;
  margin: 0 auto;
  .left-box {
    padding: 72px 35px 0 0;
    img {
      width: 105px;
      height: 105px;
    }
  }
  .right-box {
    flex: 1;
    padding-bottom: 50px;
    h1 { margin-bottom: 40px !important; }
    p {
      margin-bottom: 30px;
      font-family: ${({ theme }) => theme.fontIBMRegular};
    }
    a { color: #F0B90A; }
  }
`;

const TIMEOUT_STEP = {
  step: STEP_ENUM.TRYAGAIN,
  params: { errorType: 'timeout' },
};

export const Init = ({ config: { setNext } }) => {
  const {
    i18n,
    config: {
      type, transId, requestId, answerCount = 0,
    },
  } = useContext(StoreContext);
  const [fetching, setFetching] = useState(false);
  const fetchQuestion = async () => {
    if (fetching) return;
    setFetching(true);
    try {
      const result = await fetchQuestionList({
        type,
        transId,
        requestId,
      });
      setNext({
        step: STEP_ENUM.QUESTION,
        params: result,
      });
    } catch (error) {
      setFetching(false);
      if (error.message === '001153') {
        // timeout
        setNext(TIMEOUT_STEP);
      } else {
        Message.fail(getLabel('exchange-code-051998', i18n), TOAST_FADE_TIME);
      }
    }
  };
  return (
    <InitContainer>
      <img className="question-img" src={assetPrefix + require('../../static/images/2fa/question.svg')} alt="" />
      <h1 className="sub-title">{getLabel('exchange-reset2fa-answerquestion', i18n)}</h1>
      <p>{getLabel('exchange-flow-questiondesc', i18n)}</p>
      <div className="content">
        <Icon type="tips" size="20" color="#DCA90A" />
        <ul
          // eslint-disable-next-line
          dangerouslySetInnerHTML={{ __html: getLabel('exchange-flow-questiontip', i18n).replace('%count%', answerCount) }}
        />
      </div>
      <div className="btn-box">
        <Button
          size="lg"
          type="primary"
          loading={fetching}
          onClick={fetchQuestion}
        >{getLabel('exchange-reset2fa-startanswer', i18n)}
        </Button>
      </div>
    </InitContainer>
  );
};
Init.propTypes = {
  config: PropTypes.object,
};
// const formatStep = ({ answerResult, nextStatus, answerComplete }) => {
//   let nextStep = {
//     step: '',
//     params: {},
//   };
//   if (answerResult) {
//     nextStep.step = STEP_ENUM.PASSED;
//   } else if (answerComplete === 'TimeOut') {
//     nextStep = TIMEOUT_STEP;
//   } else {
//     nextStep = QUESTION_STATUS_MAP[nextStatus];
//   }
//   return nextStep;
// };
const NOT_ALL_OPTION = 'NONE_OF_THE_ABOVE';
const validate = optionStatus => Object.keys(optionStatus).some(key => optionStatus[key]);
const QuestionItem = ({
  question, hasNext, gotoNext,
}) => {
  const { i18n, config: { transId } } = useContext(StoreContext);
  const [optionStatus, setOptionStatus] = useState({});
  const [validated, setValidate] = useState(true);
  const [fetching, setFetching] = useState(false);
  const { options, questionId, langFlag } = question;
  const optionSelect = (option) => {
    let target = {
      [option]: !optionStatus[option],
    };
    if (option !== NOT_ALL_OPTION) {
      target = {
        ...optionStatus,
        [option]: !optionStatus[option],
        [NOT_ALL_OPTION]: false,
      };
    }
    setValidate(validate(target));
    setOptionStatus(target);
  };
  const handleSubmit = async () => {
    const finalValiate = validated && validate(optionStatus);
    setValidate(finalValiate);
    if (!finalValiate) return;
    setFetching(true);
    // answer question
    const answers = Object.keys(optionStatus).filter(key => optionStatus[key]);
    try {
      const result = await answerQuestion({
        answers,
        questionId,
        resetId: transId,
      });
      gotoNext(result);
    } catch (error) {
      setFetching(false);
      Message.fail(error.message, TOAST_FADE_TIME);
    }
  };
  const btnKey = hasNext ? 'exchange-reset2fa-next' : 'exchange-reset2fa-submit';
  return (
    <>
      <div className="question-item">
        <div className="question-title">{getLabel(langFlag, i18n)}</div>
        <div className="option-wrapper">
          {
            options.map((option, idx) => (
              <span
                key={idx}
                onClick={() => { optionSelect(option); }}
                className={`question-option ${optionStatus[option] && 'checked'}`}
              >
                {getLabel(`exchange-reset2fa-${option}`, i18n, option)}
                <Icon size={28} type="step1" color="#F0B90B" />
              </span>
            ))
          }
        </div>
      </div>
      <div className="btn-box">
        {!validated && <p className="error-line">{getLabel('exchange-reset2fa-noselect', i18n)}</p>}
        <Button
          size="lg"
          type="primary"
          loading={fetching}
          onClick={handleSubmit}
        >
          {getLabel(btnKey, i18n)}
        </Button>
      </div>
    </>
  );
};
QuestionItem.propTypes = {
  question: PropTypes.object,
  hasNext: PropTypes.bool,
  gotoNext: PropTypes.func,
};
export const Question = ({ config: { questions = [], timeRemaining, setNext } }) => {
  const { i18n } = useContext(StoreContext);
  const [curIndex, setIndex] = useState(0);
  const hasNext = curIndex + 1 < questions?.length;
  const handleNext = ({ answerComplete }) => {
    if (answerComplete === 'OK') {
      // hasnext question
      setIndex(fresh => {
        if (!hasNext) return fresh;
        return fresh + 1;
      });
    } else {
      const step = QUESTION_STATUS_MAP[answerComplete] || QUESTION_STATUS_MAP.Fail;
      setNext(step);
    }
  };
  const handleTimeout = async () => {
    setNext(TIMEOUT_STEP);
  };
  if (questions.length < 1) {
    Message.fail(getLabel('exchange-code-051998', i18n), TOAST_FADE_TIME);
    return null;
  }

  const curQuestion = questions[curIndex];

  return (
    <Container>
      <div>
        <div className="serial">
          <span className="count">{curIndex + 1}</span>
          <span className="total">&nbsp;/&nbsp;{questions?.length}</span>
        </div>
        <p className="timer">
          {getLabel('exchange-reset2fa-reminetime', i18n)}
          <Timer
            initSecond={timeRemaining}
            timeOver={handleTimeout}
          />
        </p>
        <QuestionItem
          key={curQuestion.questionId}
          hasNext={hasNext}
          i18n={i18n}
          question={curQuestion}
          gotoNext={handleNext}
        />
      </div>
    </Container>
  );
};
Question.propTypes = {
  config: PropTypes.object,
};

export const Passed = () => {
  const { i18n } = useContext(StoreContext);
  return (
    <EmailVerify
      title={getLabel('exchange-reset2fa-verifyemail', i18n)}
      desc={getLabel('exchange-reset2fa-jumioemail', i18n)}
    />
  );
};

export const VerticalBox = ({
  i18n,
  title,
  iconUrl,
  content,
  btn,
}) => (
  <InitContainer bottom="0">
    <img className="question-img" src={iconUrl} alt="" />
    <h1 className="sub-title">{getLabel(title, i18n)}</h1>
    <p
      // eslint-disable-next-line
      dangerouslySetInnerHTML={{ __html: getLabel(content, i18n) }}
    />
    <div className="btn-box">
      <Button
        size="lg"
        type="primary"
        onClick={btn.cb}
      >
        {getLabel(btn.label, i18n)}
      </Button>
    </div>
  </InitContainer>
);
VerticalBox.propTypes = {
  i18n: PropTypes.object,
  title: PropTypes.string,
  iconUrl: PropTypes.string,
  content: PropTypes.string,
  btn: PropTypes.object,
};

export const TryAgain = ({ config: { errorType } }) => {
  const { i18n, gotoNextStep } = useContext(StoreContext);
  const errorKey = errorType === 'timeout' ? 'exchange-reset2fa-timeoutdesc' : 'exchange-reset2fa-answererrorddesc';
  return (
    <VerticalBox
      i18n={i18n}
      title="exchange-reset2fa-answerfailed"
      iconUrl={assetPrefix + require('../UserCenter/sure-cancel.svg')}
      content={errorKey}
      btn={{
        label: 'exchange-reset2fa-tryagain',
        cb: gotoNextStep,
      }}
    />
  );
};
TryAgain.propTypes = {
  config: PropTypes.object,
};

export const BaseLayout = ({ iconUrl, title, content }) => {
  const { i18n, locale } = useContext(StoreContext);
  return (
    <BaseLayoutContainer>
      <div className="left-box">
        <img className="question-img" src={iconUrl} alt="" />
      </div>
      <div className="right-box">
        <h1 className="sub-title">{getLabel(title, i18n)}</h1>
        <p
          // eslint-disable-next-line
          dangerouslySetInnerHTML={{ __html: getLabel(content, i18n) }}
        />
        <Link route={`/${locale}`}>
          <a>{getLabel('exchange-reset2fa-tohome', i18n)}</a>
        </Link>
      </div>
    </BaseLayoutContainer>
  );
};
BaseLayout.propTypes = {
  iconUrl: PropTypes.string,
  title: PropTypes.string,
  content: PropTypes.string,
};

export const AnswerFailed = ({ config: { errorType } }) => {
  const errorKey = errorType === 'device' ? 'exchange-reset2fa-answerfaileddevice' : 'exchange-reset2fa-answerfailedlock';
  return (
    <BaseLayout
      iconUrl={assetPrefix + require('../../static/images/2fa/error.svg')}
      title="exchange-reset2fa-answerfailed"
      content={errorKey}
    />
  );
};
AnswerFailed.propTypes = {
  config: PropTypes.object,
};

export default {};
